/**
 * @file Rules Index
 * @description Conditionally loads Rules or NotRecording based on recording status
 * 
 * This wrapper component acts as a router that:
 * 1. Checks if recording is active on mount
 * 2. Shows NotRecording component if recording is not active (handles initialization)
 * 3. Shows Rules component if recording is active (normal flow)
 * 
 * @module context/session/components/Rules
 */

import React, { useEffect, useState } from 'react';
import { useAppStore } from '../../stores/app';
import SinglePane from '../SinglePane';
import Rules from './Rules';
import NotRecording from './NotRecording';
import Loader from '../Common/Loader';

/**
 * Rules Wrapper Component
 * 
 * Acts as a smart router that determines which component to display based on
 * the current recording status. This ensures users cannot proceed with rules
 * acknowledgment until recording is confirmed active.
 * 
 * Flow:
 * 1. On mount, fetches recording info from server
 * 2. Checks if recordingInfo.active === true
 * 3. Routes to appropriate component:
 *    - NotRecording: Handles recording initialization and polling
 *    - Rules: Normal rules acknowledgment flow
 * 
 * @param {Object} props - Props to pass through to Rules component
 * @returns {React.ReactElement} Loader, NotRecording, or Rules component
 */
function RulesWrapper(props) {
  // Track loading state while checking recording status
  const [isLoading, setIsLoading] = useState(true);
  // Track whether recording is active (null = not yet checked)
  const [isRecordingActive, setIsRecordingActive] = useState(null);
  // Get recording info from app store
  const { getRecordingInfo } = useAppStore();

  // Check recording status on component mount
  useEffect(() => {
    /**
     * Fetches recording info and determines if recording is active
     * Forces reload (true) to get fresh data from server
     */
    const checkRecordingStatus = async () => {
      // Force reload to get latest recording status from server
      const recordingInfo = await getRecordingInfo(true);
      // Recording is active only if explicitly set to true
      const isActive = recordingInfo?.active === true;
      setIsRecordingActive(isActive);
      setIsLoading(false);
    };

    checkRecordingStatus();
  }, [getRecordingInfo]);

  // Show loader while checking recording status
  if (isLoading || isRecordingActive === null) {
    return (
      <SinglePane>
        <Loader text='Verifying Recording Status...' />
      </SinglePane>
    );
  }

  // Recording is not active - show NotRecording component
  // This component will attempt to start recording and poll for confirmation
  if (!isRecordingActive) {
    return <NotRecording />;
  }

  // Recording is active - show normal Rules component
  // Pass through all props to Rules component
  return <Rules {...props} />;
}

export default RulesWrapper;
